'use client';

import { useCallback, useMemo, useEffect, useRef } from 'react';
import { splitSiteOfCareValues } from 'src/components/FilterSets/SiteOfCare/utils';

const EMPTY_COLUMN_IDS: string[] = [];
import {
  applyFilterSetSelections,
  resetFilterSetSelectionsToApplied,
  setColumnId as setColumnIdAction,
  setMode as setModeAction,
  setType as setTypeAction,
  setFilterSelectionsDateRange as setDateRangeAction,
  setBilling as setBillingAction,
  setIsTrending as setIsTrendingAction,
  setPlaceOfServiceFilter as setPlaceOfServiceFilterAction,
  setPayerTypeFilter as setPayerTypeFilterAction,
  setSiteOfCareFilter as setSiteOfCareFilterAction,
  clearFilterType as clearFilterTypeAction,
  removeFilter as removeFilterAction,
  updateColumnId as updateColumnIdAction,
  setCurrentTableType as setCurrentTableTypeAction,
  useAppDispatch,
  useAppSelector,
  selectTempColumnId,
  selectAppliedColumnId,
  selectEditingColumnId,
  selectTempColumnSettings,
  selectAppliedColumnSettings,
  selectAllTempColumnSettings,
  selectAllAppliedColumnSettings,
  selectMode,
  selectType,
  selectFilterSelectionsDateRange as selectDateRange,
  selectBilling,
  selectCodeSet,
  selectCohortId,
  selectIsTrending,
  selectTempIsTrending,
  selectCurrentTableType,
  // Import Redux actions and selectors for filter set presets from filterSelectionsSlice
  setSelectedFilterPreset,
  setTempSelectedFilterPreset,
  clearFilterPreset,
  // Import selectAppliedFilterSet to access filter set data
  selectAppliedFilterSet,
  // Import clearFilter action from filterSetsSlice
  clearFilter,
} from 'src/redux';
import { MIN_VOLUME_YEAR, MAX_VOLUME_YEAR, DEFAULT_DATE_RANGE } from 'src/components/constants';
import { FilterSetSelections } from 'src/context/reducers/filterSetReducer';
import { UseTableFilterSelectionsReturn } from './types';
import { ParentFilterGroup } from 'src/context/reducers/filterSetReducer';

/**
 * Utility functions for handling instance IDs
 * Instance IDs follow the pattern: {baseId}_{uuid}
 * e.g., "4383_550e8400-e29b-41d4-a716-446655440000"
 */

/**
 * Extracts the base ID from an instance ID
 * @param instanceId - The instance ID (e.g., "4383_550e8400-e29b-41d4-a716-446655440000")
 * @returns The base ID (e.g., "4383")
 */
export const extractBaseId = (instanceId: string): string => {
  // Instance IDs are in format: baseId_uuid
  const parts = instanceId.split('_');
  return parts[0];
};

/**
 * Resolves the volume `sort_field` for a column, swapping claims → encounters for
 * encounters (cohort) columns when the `show-encounters-totals` flag is on. Mirrors
 * the display swap so a cohort column sorts by the same metric it renders. Only cohort
 * columns accept `sort_field=encounters`; code-set columns keep their type.
 * @param settings - the column's entry from `apiVolumeFilterFormat` (has `type` + optional `cohort_id`)
 * @param showEncountersTotals - the LaunchDarkly flag value
 */
export const resolveVolumeSortField = (
  settings: { type?: string; cohort_id?: number } | undefined,
  showEncountersTotals: boolean
): string | null => {
  const type = settings?.type ?? null;
  if (showEncountersTotals && settings?.cohort_id && type === 'claims') {
    return 'encounters';
  }
  return type;
};

/**
 * Custom hook for managing table filter set selections across different locations.
 * Handles state management, persistence, and synchronization of filter set selections.
 */
const useTableFilterSelections = (
  tableType?: string
): UseTableFilterSelectionsReturn => {
  const dispatch = useAppDispatch();

  // Use the slice's isHydrated flag instead of localStorage checks.
  // localStorage is no longer written to by the new persistence system,
  // so the old localStorage-based isInitialized was reading stale data
  // and causing column flicker that destabilized DataGrid.
  const isInitialized = useAppSelector(
    (state) => state.filterSelections.isHydrated
  );

  // Get the current table type from Redux
  const currentTableType = useAppSelector(selectCurrentTableType);

  // Use the location directly
  const activeTableType = tableType || currentTableType;

  const selectedFilterPreset = useAppSelector((state) => {
    const tableState = state.filterSelections.tables?.[activeTableType];
    return tableState?.selectedFilterPreset || null;
  });
  const tempSelectedFilterPreset = useAppSelector((state) => {
    const tableState = state.filterSelections.tables?.[activeTableType];
    return tableState?.tempSelectedFilterPreset || null;
  });

  // Use ref to track previous table type to avoid infinite loops
  const previousTableTypeRef = useRef<string | null>(null);

  // Set the current table type in Redux if a tableType is provided
  useEffect(() => {
    if (tableType && previousTableTypeRef.current !== tableType) {
      previousTableTypeRef.current = tableType;
      dispatch(setCurrentTableTypeAction({ tableType }));
    }
  }, [tableType, dispatch]);

  // Industry-standard approach: Use table-specific selectors
  // This ensures we always get the correct state for the specific table type

  const tempColumnId = useAppSelector((state) =>
    selectTempColumnId(state, activeTableType)
  );

  const appliedColumnId = useAppSelector((state) =>
    selectAppliedColumnId(state, activeTableType)
  );

  const editingColumnId = useAppSelector((state) =>
    selectEditingColumnId(state, activeTableType)
  );

  const isTrending = useAppSelector((state) =>
    selectIsTrending(state, activeTableType)
  );

  const tempIsTrending = useAppSelector((state) =>
    selectTempIsTrending(state, activeTableType)
  );

  // Use the table-specific selectors for column settings
  const tempColumnSettings = useAppSelector((state) =>
    selectTempColumnSettings(state, activeTableType)
  );

  const appliedColumnSettings = useAppSelector((state) =>
    selectAppliedColumnSettings(state, activeTableType)
  );

  // Use the table-specific selectors for individual column settings
  const mode = useAppSelector((state) => {
    const tableState = state.filterSelections.tables?.[activeTableType];
    if (!tableState) return 'treating';

    // If no column selected, use tempMode
    if (!tableState.tempColumnId) {
      return tableState.tempMode || 'treating';
    }

    // Otherwise use the mode from the current column settings
    return selectMode(state, activeTableType);
  });
  const type = useAppSelector((state) => selectType(state, activeTableType));
  const date_range = useAppSelector((state) =>
    selectDateRange(state, activeTableType)
  );
  const billing = useAppSelector((state) =>
    selectBilling(state, activeTableType)
  );
  const code_set = useAppSelector((state) =>
    selectCodeSet(state, activeTableType)
  );
  const cohort_id = useAppSelector((state) =>
    selectCohortId(state, activeTableType)
  );

  /**
   * Checks if the temporary filter set selections are different from the applied ones
   */
  /**
   * Helper function to check if two arrays have the same elements
   * regardless of order
   */
  const arraysEqual = useCallback((a: number[], b: number[]) => {
    if (a.length !== b.length) return false;

    // Create a copy of b to track which elements have been matched
    const bCopy = [...b];

    // For each element in a, find and remove the matching element in bCopy
    for (const item of a) {
      const index = bCopy.indexOf(item);
      if (index === -1) return false;
      bCopy.splice(index, 1);
    }

    // If we've matched all elements, bCopy should be empty
    return bCopy.length === 0;
  }, []);

  /**
   * Helper function to compare filter value arrays efficiently
   */
  const filterValuesEqual = useCallback((a: any[], b: any[]) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;

    const valuesA = a.map((v) => v.value).sort();
    const valuesB = b.map((v) => v.value).sort();

    return valuesA.every((val, index) => val === valuesB[index]);
  }, []);

  // Use the table-specific selectors for all column settings
  const allTempColumnSettings = useAppSelector((state) =>
    selectAllTempColumnSettings(state, activeTableType)
  );

  // This will automatically update when activeTableType changes because it's used in the selector
  const allAppliedColumnSettings = useAppSelector((state) =>
    selectAllAppliedColumnSettings(state, activeTableType)
  );

  const isSelectionsDirty = useCallback(() => {
    // Compare column IDs - if they're different, we're selecting a different column
    if (tempColumnId !== appliedColumnId) {
      return true;
    }

    // If editing an existing column (IDs match), compare the actual settings
    // With instance IDs, the IDs stay the same while editing, so we need to compare settings
    if (tempColumnId && appliedColumnId && tempColumnId === appliedColumnId) {
      const tempSettings = tempColumnSettings;
      const appliedSettings = appliedColumnSettings;

      // Compare mode (treating vs sending)
      if (tempSettings.mode !== appliedSettings.mode) {
        return true;
      }

      // Compare type (claims vs patients)
      // This will correctly detect: claims→patients (dirty) and patients→claims (not dirty if started with claims)
      if (tempSettings.type !== appliedSettings.type) {
        return true;
      }

      // Compare date_range
      if (
        tempSettings.date_range.gte !== appliedSettings.date_range.gte ||
        tempSettings.date_range.lte !== appliedSettings.date_range.lte
      ) {
        return true;
      }

      // Compare filter_sets
      if (!arraysEqual(tempSettings.filter_sets, appliedSettings.filter_sets)) {
        return true;
      }

      // Compare placeOfServiceFilter
      const tempPlaceOfService = tempSettings.placeOfServiceFilter || {
        type: null,
        is_excluded: false,
        is_all: false,
        values: [],
      };
      const appliedPlaceOfService = appliedSettings.placeOfServiceFilter || {
        type: null,
        is_excluded: false,
        is_all: false,
        values: [],
      };
      if (
        tempPlaceOfService.is_excluded !== appliedPlaceOfService.is_excluded ||
        tempPlaceOfService.is_all !== appliedPlaceOfService.is_all ||
        !filterValuesEqual(
          tempPlaceOfService.values || [],
          appliedPlaceOfService.values || []
        )
      ) {
        return true;
      }

      // Compare payerTypeFilter
      const tempPayerType = tempSettings.payerTypeFilter || {
        type: null,
        is_excluded: false,
        is_all: false,
        values: [],
      };
      const appliedPayerType = appliedSettings.payerTypeFilter || {
        type: null,
        is_excluded: false,
        is_all: false,
        values: [],
      };
      if (
        tempPayerType.is_excluded !== appliedPayerType.is_excluded ||
        tempPayerType.is_all !== appliedPayerType.is_all ||
        !filterValuesEqual(
          tempPayerType.values || [],
          appliedPayerType.values || []
        )
      ) {
        return true;
      }

      // Compare siteOfCareFilter (fall back to empty default for state persisted
      // before the field existed, so old/new shapes compare cleanly)
      const tempSiteOfCare = tempSettings.siteOfCareFilter || {
        type: null,
        is_excluded: false,
        is_all: false,
        values: [],
      };
      const appliedSiteOfCare = appliedSettings.siteOfCareFilter || {
        type: null,
        is_excluded: false,
        is_all: false,
        values: [],
      };
      if (
        tempSiteOfCare.is_excluded !== appliedSiteOfCare.is_excluded ||
        tempSiteOfCare.is_all !== appliedSiteOfCare.is_all ||
        !filterValuesEqual(
          tempSiteOfCare.values || [],
          appliedSiteOfCare.values || []
        )
      ) {
        return true;
      }

      // Compare code_set
      if (
        (tempSettings.code_set !== undefined &&
          appliedSettings.code_set === undefined) ||
        (tempSettings.code_set === undefined &&
          appliedSettings.code_set !== undefined) ||
        (tempSettings.code_set !== undefined &&
          appliedSettings.code_set !== undefined &&
          tempSettings.code_set !== appliedSettings.code_set)
      ) {
        return true;
      }

      // Compare cohort_id
      if (
        (tempSettings.cohort_id !== undefined &&
          appliedSettings.cohort_id === undefined) ||
        (tempSettings.cohort_id === undefined &&
          appliedSettings.cohort_id !== undefined) ||
        (tempSettings.cohort_id !== undefined &&
          appliedSettings.cohort_id !== undefined &&
          tempSettings.cohort_id !== appliedSettings.cohort_id)
      ) {
        return true;
      }
    }

    // Compare all column settings
    const tempColumnIds = Object.keys(allTempColumnSettings);
    const appliedColumnIds = Object.keys(allAppliedColumnSettings);

    // If different number of columns, something changed
    if (tempColumnIds.length !== appliedColumnIds.length) {
      return true;
    }

    // Check if all temp column IDs exist in applied
    for (const id of tempColumnIds) {
      if (!appliedColumnIds.includes(id)) {
        return true;
      }
    }

    // Compare filter presets
    if (tempSelectedFilterPreset?.id !== selectedFilterPreset?.id) {
      return true;
    }

    // Compare trending state
    if (tempIsTrending !== isTrending) {
      return true;
    }

    return false;
  }, [
    tempColumnId,
    appliedColumnId,
    tempColumnSettings,
    appliedColumnSettings,
    allTempColumnSettings,
    allAppliedColumnSettings,
    arraysEqual,
    filterValuesEqual,
    tempSelectedFilterPreset,
    selectedFilterPreset,
    tempIsTrending,
    isTrending,
  ]);

  /**
   * Applies the current temporary filter set selections
   */
  const applySelections = useCallback(() => {
    // Apply filter selections for the active table type
    dispatch(applyFilterSetSelections({ tableType: activeTableType }));

    // Apply the filter set preset
    if (tempSelectedFilterPreset) {
      dispatch(setSelectedFilterPreset(tempSelectedFilterPreset));
    } else {
      dispatch(setSelectedFilterPreset(null));
    }
  }, [dispatch, activeTableType, tempSelectedFilterPreset]);

  /**
   * Resets temporary filter set selections to match the currently applied ones
   */
  const resetSelections = useCallback(() => {
    dispatch(resetFilterSetSelectionsToApplied({ tableType: activeTableType }));

    // Reset the filter set preset
    if (selectedFilterPreset) {
      dispatch(setTempSelectedFilterPreset(selectedFilterPreset));
    } else {
      dispatch(setTempSelectedFilterPreset(null));
    }
  }, [dispatch, activeTableType, selectedFilterPreset]);

  /**
   * Sets the column ID and optionally specifies if it's a code_set or cohort_id
   * @param id The column ID (will be used as the value for code_set or cohort_id if type is provided)
   * @param type Optional type parameter ('code_set' or 'cohort_id')
   * @param isEditing Optional flag to indicate if we're editing an existing column
   */
  const setColumnId = useCallback(
    (
      id: string | null,
      type?: 'code_set' | 'cohort_id',
      isEditing?: boolean
    ) => {
      dispatch(
        setColumnIdAction({
          columnId: id,
          type,
          tableType: activeTableType,
          isEditing: isEditing || false, // Pass the isEditing flag to Redux
        })
      );
    },
    [dispatch, activeTableType]
  );

  /**
   * Sets the mode (treating or sending) for the current column
   */
  const setMode = useCallback(
    (newMode: 'treating' | 'sending') => {
      dispatch(setModeAction({ mode: newMode, tableType: activeTableType }));
    },
    [dispatch]
  );

  /**
   * Sets the type (claims or patients) for the current column
   */
  const setType = useCallback(
    (newType: 'claims' | 'patients') => {
      dispatch(setTypeAction({ type: newType, tableType: activeTableType }));
    },
    [dispatch, activeTableType]
  );

  /**
   * Sets the date_range for the current column
   */
  const setDateRange = useCallback(
    (newDateRange: { gte: string; lte: string }) => {
      dispatch(
        setDateRangeAction({
          dateRange: newDateRange,
          tableType: activeTableType,
        })
      );
    },
    [dispatch, activeTableType]
  );

  /**
   * Simple API filter format with just the selected provider preset ID
   * The left side of the table - uses applied state with preset-first approach
   */
  const apiFilterFormat = useMemo(() => {
    return selectedFilterPreset?.id ? [selectedFilterPreset.id] : [];
  }, [selectedFilterPreset]);

  // Get the applied filter set to access place of service and payer type filters
  const appliedFilterSet = useAppSelector(selectAppliedFilterSet);

  /**
   * Table columnIds to make table hydration easier
   * Returns composite keys as strings for unique column identification
   * Declared here (before apiVolumeFilterFormat) so it can be used to preserve column order
   */
  const columnIds = useAppSelector((state) => {
    const tableState = state.filterSelections.tables?.[activeTableType];
    return tableState?.columnIds || EMPTY_COLUMN_IDS;
  });

  /**
   * Volume-specific API filter format organized by columnId
   * the right (teal) side of the table - only uses applied state
   * Keys are ordered by columnIds to preserve the user's configured column order
   */
  const apiVolumeFilterFormat = useMemo(() => {
    // Create an object with columnId as key
    const result: Record<
      string,
      {
        mode: 'treating' | 'sending';
        type: 'claims' | 'patients';
        date_range: { gte: number; lte: number };
        filter_sets: number[];
        filters: {
          type: string;
          is_exclude: boolean;
          is_all: boolean;
          values: string[];
        }[];
        code_set?: number;
        cohort_id?: number;
      }
    > = {};

    // Use columnIds to preserve the user's configured column order
    const allColumnIds = columnIds;

    // For each columnId, create an entry in the result object
    allColumnIds.forEach((id) => {
      // Get the column settings for this columnId
      const settings = allAppliedColumnSettings[id] || {
        mode: 'treating' as const,
        type: 'claims' as const,
        date_range: { ...DEFAULT_DATE_RANGE },
        filter_sets: [],
        filters: [],
        code_set: undefined,
        cohort_id: undefined,
        placeOfServiceFilter: {
          type: null,
          is_excluded: false,
          is_all: false,
          values: [],
        },
        payerTypeFilter: {
          type: null,
          is_excluded: false,
          is_all: false,
          values: [],
        },
        siteOfCareFilter: {
          type: null,
          is_excluded: false,
          is_all: false,
          values: [],
        },
      };

      // Start with existing filter_sets array or empty array
      const filterSetsArray = [...(settings.filter_sets || [])];

      const filtersArray = [];

      // No longer adding billing preset ID to filter_sets as billing types no longer exist

      // Site of Care replaces the legacy Place of Service filter. The composite
      // SOC values are split into separate SOC_TYPE / SOC_SUBTYPE backend rows
      // (mirrors the filter-set emission in filterSetsSlice).
      if (settings.siteOfCareFilter?.values?.length > 0) {
        const { socType, socSubtype } = splitSiteOfCareValues(
          settings.siteOfCareFilter.values
        );
        if (socType.length > 0) {
          filtersArray.push({
            type: 'SOC_TYPE',
            is_exclude: settings.siteOfCareFilter.is_excluded,
            is_all: settings.siteOfCareFilter.is_all,
            values: socType,
          });
        }
        if (socSubtype.length > 0) {
          filtersArray.push({
            type: 'SOC_SUBTYPE',
            is_exclude: settings.siteOfCareFilter.is_excluded,
            is_all: settings.siteOfCareFilter.is_all,
            values: socSubtype,
          });
        }
      }

      // Add payer type filter if it has values in column settings
      if (settings.payerTypeFilter?.values?.length > 0) {
        filtersArray.push({
          type: 'PAYER_TYPE',
          is_exclude: settings.payerTypeFilter.is_excluded,
          is_all: settings.payerTypeFilter.is_all,
          values: settings.payerTypeFilter.values.map((v) => v.value),
        });
      }

      // Use the full composite key to ensure uniqueness (e.g., "4383_treating")
      const compositeKey = id;

      // Add entry to result object using composite key
      result[compositeKey] = {
        mode: settings.mode,
        type: settings.type,
        // Convert date_range strings to numbers with fallbacks
        date_range: {
          gte: parseInt(settings.date_range.gte, 10) || MIN_VOLUME_YEAR,
          lte: parseInt(settings.date_range.lte, 10) || MAX_VOLUME_YEAR,
        },
        filter_sets: filterSetsArray,
        filters: filtersArray,
      };

      // Add code_set or cohort_id if they exist
      if (settings.code_set !== undefined) {
        result[compositeKey].code_set = settings.code_set;
      }
      if (settings.cohort_id !== undefined) {
        result[compositeKey].cohort_id = settings.cohort_id;
      }
    });

    return result;
  }, [
    columnIds,
    appliedColumnId,
    appliedColumnSettings,
    allAppliedColumnSettings,
    appliedFilterSet,
  ]);

  /**
   * Clears all selected filter sets for a specific filter type
   */
  const clearFilterType = useCallback(
    (filterType: keyof FilterSetSelections) => {
      dispatch(
        clearFilterTypeAction({ filterType, tableType: activeTableType })
      );
    },
    [dispatch, activeTableType]
  );

  /**
   * Sets the billing filter for the current column
   * @deprecated With preset-first approach, prefer using billing preset functions instead
   */
  const setBilling = useCallback(
    (id: number, operation: 'add' | 'remove' | 'set' | 'clear' = 'set') => {
      dispatch(setBillingAction({ id, operation, tableType: activeTableType }));
    },
    [dispatch, activeTableType]
  );

  const setIsTrending = useCallback(
    (isTrending: boolean) => {
      dispatch(setIsTrendingAction({ isTrending, tableType: activeTableType }));
    },
    [dispatch, activeTableType]
  );

  /**
   * Clears all filters for the current column
   * @deprecated With preset-first approach, prefer using clearFilterPreset() instead
   */
  const clearBilling = useCallback(() => {
    dispatch(
      setBillingAction({
        id: 0,
        operation: 'clear',
        tableType: activeTableType,
      })
    );
  }, [dispatch, activeTableType]);

  /**
   * Removes the filter for the current column
   */
  const removeFilter = useCallback(
    (columnId: string) => {
      dispatch(removeFilterAction({ columnId, tableType: activeTableType }));
    },
    [dispatch, activeTableType]
  );

  /**
   * Updates the columnId
   */
  const updateColumnId = useCallback(
    (newColumnId: string, type?: 'code_set' | 'cohort_id') => {
      dispatch(
        updateColumnIdAction({
          columnId: newColumnId,
          type,
          tableType: activeTableType,
        })
      );
    },
    [dispatch, activeTableType]
  );

  // Provider and billing presets no longer exist

  /**
   * Gets the currently selected filter set (for backward compatibility)
   */
  const getSelectedFilterSet = useCallback(() => {
    // Only return filter preset as provider and billing types no longer exist
    return selectedFilterPreset;
  }, [selectedFilterPreset]);

  /**
   * Sets the selected preset based on type
   */
  const selectFilterSet = useCallback(
    (filterSet: ParentFilterGroup | null) => {
      if (filterSet?.type === 'FILTER') {
        // Dispatch Redux actions for FILTER type
        dispatch(setTempSelectedFilterPreset(filterSet));
      } else {
        // If null or unknown type, clear filter preset
        dispatch(setTempSelectedFilterPreset(null));
      }
    },
    [dispatch]
  );

  /**
   * Gets the temporary filter set IDs for a given filter type
   */
  const getTempFilterSetId = useCallback(
    (_filterType: keyof FilterSetSelections): number[] => {
      // Use the existing tempColumnId and tempColumnSettings that are already available
      if (!tempColumnId) return [];

      // Return the filter_sets array (which contains the IDs)
      return tempColumnSettings.filter_sets || [];
    },
    [tempColumnId, tempColumnSettings]
  );

  /**
   * Gets the applied filter set IDs for a given filter type
   */
  const getAppliedFilterSetId = useCallback(
    (_filterType: keyof FilterSetSelections): number[] => {
      // Use the existing appliedColumnId and appliedColumnSettings that are already available
      if (!appliedColumnId) return [];

      // Return the filter_sets array (which contains the IDs)
      return appliedColumnSettings.filter_sets || [];
    },
    [appliedColumnId, appliedColumnSettings]
  );

  // Create wrapper functions for Redux actions following the established pattern

  const setSelectedFilterPresetFn = useCallback(
    (preset: ParentFilterGroup | null) => {
      dispatch(setSelectedFilterPreset(preset));
    },
    [dispatch]
  );

  const setTempSelectedFilterPresetFn = useCallback(
    (preset: ParentFilterGroup | null) => {
      dispatch(setTempSelectedFilterPreset(preset));
    },
    [dispatch]
  );

  // Clear functions that clear both temp and selected

  const clearFilterPresetFn = useCallback(() => {
    dispatch(clearFilterPreset({ tableType: activeTableType }));
  }, [dispatch]);

  // Reset to applied (applied -> temp)
  const resetToApplied = useCallback(() => {
    // Reset the filter selections
    dispatch(resetFilterSetSelectionsToApplied({ tableType: activeTableType }));

    // Reset the filter set preset
    if (selectedFilterPreset) {
      dispatch(setTempSelectedFilterPreset(selectedFilterPreset));
    } else {
      dispatch(setTempSelectedFilterPreset(null));
    }
  }, [dispatch, activeTableType, selectedFilterPreset]);

  // Use type assertion to satisfy the TypeScript interface
  /**
   * Clears filters only for the current column
   */
  const clearColumnFilters = useCallback(() => {
    if (!tempColumnId) return;

    // Clear the filter_sets array for the current column using the existing setBillingAction
    dispatch(
      setBillingAction({
        id: 0,
        operation: 'clear',
        tableType: activeTableType,
      })
    );

    // Use the clearFilter action to clear place of service, payer type, and
    // site of care filters
    dispatch(clearFilter({ type: 'placeOfService' }));
    dispatch(clearFilter({ type: 'payerType' }));
    dispatch(clearFilter({ type: 'siteOfCare' }));

    // No need to clear billing preset as it no longer exists
  }, [
    dispatch,
    activeTableType,
    tempColumnId,
    tempColumnSettings,
    setBillingAction,
    clearFilter,
  ]);

  // Add functions to set place of service and payer type filters
  const setPlaceOfServiceFilter = useCallback(
    (filter: {
      type: any | null;
      is_excluded: boolean;
      is_all: boolean;
      values: { value: string; label: string }[];
    }) => {
      dispatch(
        setPlaceOfServiceFilterAction({
          filter,
          tableType: activeTableType,
        })
      );
    },
    [dispatch, activeTableType]
  );

  const setPayerTypeFilter = useCallback(
    (filter: {
      type: any | null;
      is_excluded: boolean;
      is_all: boolean;
      values: { value: string; label: string }[];
    }) => {
      dispatch(
        setPayerTypeFilterAction({
          filter,
          tableType: activeTableType,
        })
      );
    },
    [dispatch, activeTableType]
  );

  const setSiteOfCareFilter = useCallback(
    (filter: {
      type: any | null;
      is_excluded: boolean;
      is_all: boolean;
      values: { value: string; label: string }[];
    }) => {
      dispatch(
        setSiteOfCareFilterAction({
          filter,
          tableType: activeTableType,
        })
      );
    },
    [dispatch, activeTableType]
  );

  return {
    currentTableType: activeTableType,
    clearFilterType,
    isSelectionsDirty,
    applySelections,
    resetSelections,
    apiFilterFormat,
    apiVolumeFilterFormat,
    columnIds,
    columnId: tempColumnId, // For backward compatibility
    setColumnId,
    mode,
    setMode,
    type,
    setType,
    date_range,
    setDateRange,
    billing,
    setBilling,
    clearBilling,
    clearColumnFilters, // Add the new function
    setPlaceOfServiceFilter, // Add the new function
    setPayerTypeFilter, // Add the new function
    setSiteOfCareFilter, // Add the new function
    resetToApplied,
    code_set,
    cohort_id,
    removeFilter,
    updateColumnId,
    isTrending,
    tempIsTrending,
    setIsTrending,
    isInitialized, // Export initialization state so components can check it

    // Add these for ColumnSelectorDropdown
    allTempColumnSettings, // Add this line
    allAppliedColumnSettings, // Add this line (if needed)
    editingColumnId, // Add editing column ID to determine if we're editing vs creating

    // Use the actual implementations instead of stubs
    selectFilterSet,
    getSelectedFilterSet,
    getTempFilterSetId,
    getAppliedFilterSetId,

    // Filter preset functions - only FILTER type exists now
    selectedFilterPreset,
    tempSelectedFilterPreset,
    setSelectedFilterPreset: setSelectedFilterPresetFn,
    setTempSelectedFilterPreset: setTempSelectedFilterPresetFn,
    getSelectedFilterPreset: () => selectedFilterPreset,

    // Clear functions
    clearFilterPreset: clearFilterPresetFn,
  } as UseTableFilterSelectionsReturn;
};

export default useTableFilterSelections;
